import {_, React, observer, observable, computed, IDisposer, action, useLocalStore, useEffect, useLayoutEffect, classNames, useAsObservableSource, Fragment} from '@api'
import {MenuDivider, MenuItem} from '@blueprintjs/core'
import {Mark} from '@material-ui/core'
import {CardSizeSlider, CatalogColumnSliderMaterial, ContextMenuItem, useStores} from '@modules'
import {DEFAULT_CATALOG_USER_SETTINGS, icons, StoresInstance} from '@stores'
import {AssortmentLayoutSettings} from '@stores/catalog'
import * as css from './AssortmentContextMenu.css'

export const AssortmentLayoutContextMenuItem = observer(function AssortmentLayoutContextMenuItem() {
  return <ContextMenuItem icon={icons.assortmentLayout} text='Assortment Layout'
                          popoverProps={{ className: css.layoutSettingsMenuPopover, ...MenuItem.defaultProps.popoverProps}}
                          className={css.layoutSettingsMenu}>
    <AssortmentLayoutContextMenuItemContents />
  </ContextMenuItem>
})

export const AssortmentLayoutContextMenuItemContents = observer(function AssortmentLayoutContextMenuItemContents() {
  const {catalog: {assortments, settings: {assortment: {hero, layout, hero: {hideEmptyRows, secondLevel}}}}} = useStores()

  return (<Fragment>
      <div className={css.row}>
        <ContextMenuItem active={secondLevel.wrap}
                         text={'Wrap Second Level Cards'}
                         onClick={() => secondLevel.wrap = !secondLevel.wrap}/>
        <ContextMenuItem active={hideEmptyRows === true}
                         text={'Hide Empty Hero Rows'}
                         onClick={() => hero.hideEmptyRows = !hideEmptyRows}/>
        <ContextMenuItem icon='clean' text='Reset to Defaults' onClick={assortments.resetSettingsToDefault}/>

      </div>

      <AssortmentLayoutSettingsMenuItems/>
    </Fragment>)
})

export type LayoutSettingsDescriptor = {
  label: string,
  min: number,
  max: number,
  step: number
  marks?: boolean | Mark[],
  defaultValue?: number
}

export const layoutSettingDescriptors: Record<keyof AssortmentLayoutSettings, LayoutSettingsDescriptor> = {
  rootFontSize: {
    label: 'Scale', min: 4, max: 20, step: 1, defaultValue: 10
  },
  columns: {
    label: '# of Columns', min: 1, max: 5, step: 1, marks: true, defaultValue: 2
  },
  columnWidth: {
    label: 'Card Width',
    min: 10, max: 50, step: .25, defaultValue: 28
  },
  heroHeight: {
    label: 'Card Height',
    min: 10, max: 50, step: .25, defaultValue: 16.75
  },
  assortmentPadding: {
    label: 'Padding', min: 0, max: 6, step: .5, marks: true, defaultValue: 1
  },
  heroGap: {
    label: 'Spacing', min: 0, max: 5, step: .5, marks: true, defaultValue: 1
  },
  swatchesPerColumn: {
    label: 'Swatches Per Column', defaultValue: 3,
    min: 1, max: 6, step: 1, marks: true // [{value: 1, label: '1'}, {value: 3, label: '3'}]
  },
  heroesPerAssortment: {
    label: 'Cards per Board', defaultValue: 7,
    min: 1, max: 11, step: 1, marks: true
  },
  secondLevelHeight: {
    label: '2nd Level Height',
    min: .75, max: 5, step: .25, defaultValue: 1
  }
}

export const AssortmentLayoutSettingContextMenuItem = observer<{ setting: keyof AssortmentLayoutSettings }>(function AssortmentLayoutSettingContextMenuItem({setting: key}) {
  const stores = useStores(),
    {catalog: {assortments: {selectedAssortment}, settings: {assortment: {layout}}}} = stores,
    value = layout[key],
    defaultValue = _.get(selectedAssortment?.layout, key) ?? layoutSettingDescriptors[key].defaultValue ?? DEFAULT_CATALOG_USER_SETTINGS.assortment.layout[key],
    {label, min, max, step, marks} = layoutSettingDescriptors[key]

  return (<div className={css.settingCell}>
    <MenuDivider className={css.layoutSettingsTitle} title={label}/>
    <ContextMenuItem
      className={css.sliderMenuItem}
      text={<CardSizeSlider
        value={value}
        hideIcons
        marks={marks}
        min={min} max={max}
        onChange={(e, v: number) => layout[key] = v}
        onDoubleClick={() => layout[key] = defaultValue}
        //tooltipTitle={label}
        //valueLabelDisplay='auto'
        step={step}
      />}

    />
  </div>)
})

export const AssortmentLayoutSettingsMenuItems = observer(function AssortmentLayoutSettingsMenuItems() {
  return (<Fragment>
    <div className={css.slidersGrid}>
      <AssortmentLayoutSettingContextMenuItem setting='rootFontSize'/>
      <AssortmentLayoutSettingContextMenuItem setting='columns'/>
      <AssortmentLayoutSettingContextMenuItem setting='columnWidth'/>
      <AssortmentLayoutSettingContextMenuItem setting='assortmentPadding'/>
      <AssortmentLayoutSettingContextMenuItem setting='heroGap'/>
      <AssortmentLayoutSettingContextMenuItem setting='heroHeight'/>
      <AssortmentLayoutSettingContextMenuItem setting='heroesPerAssortment'/>
      <AssortmentLayoutSettingContextMenuItem setting='swatchesPerColumn'/>
      <AssortmentLayoutSettingContextMenuItem setting='secondLevelHeight'/>
    </div>
  </Fragment>)
})


// WEBPACK FOOTER //
// ./src/modules/catalog/internal/gridList/components/groupBy/GroupByCatalog/Assortments/AssortmentContextMenu.tsx